/**
 * BriefingLine — one line of focus for the sitting (W12 box 2).
 *
 * The instructor's word in shift language, sourced from what's actually
 * live: this week's drill (until passed) → a pending assignment → a live
 * special event → the next tier target. One line, one door named, never
 * a second paragraph. Hides entirely when there's nothing to say.
 */

import { useEffect, useState } from 'react';
import { doc, onSnapshot, collection, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { initFirebaseClient } from '@copapp/firebase/client';
import { reportCrash } from '../../lib/crash';
import { useAuth } from '../../contexts/AuthContext';
import { useSessionStore } from '../../stores';
import { COLLECTIONS, SUBCOLLECTIONS } from '@copapp/core/constants';
import { getWeeklyDrillStatus, drillTitle } from '../../services/weeklyDrill';

export function BriefingLine() {
  const { user } = useAuth();
  const { officer } = useSessionStore();
  const [line, setLine] = useState<string | null>(null);

  const departmentId = (officer as { departmentId?: string | null } | null)?.departmentId ?? null;

  useEffect(() => {
    if (!user?.uid) return;
    let cancelled = false;
    // Hoisted so cleanup can reach it — scoped inside the IIFE, the listener
    // survived unmount until its first callback fired.
    let unsubStanding: (() => void) | undefined;
    const uid = user.uid;

    (async () => {
      const { db, functions } = initFirebaseClient();

      // 1. This week's drill, until it's passed.
      if (departmentId) {
        try {
          const status = await getWeeklyDrillStatus(departmentId, uid);
          if (status && !status.completion?.passed) {
            if (!cancelled) setLine(
              `Today's focus: run this week's drill — ${drillTitle(status.config)}, pass ${status.config.passTarget}/10.`,
            );
            return;
          }
        } catch (e) { reportCrash(e); /* next source */ }
      }

      // 2. A pending assignment addressed to you.
      if (departmentId) {
        try {
          const snap = await getDocs(query(
            collection(db, COLLECTIONS.DEPARTMENTS, departmentId, SUBCOLLECTIONS.DOJO_ASSIGNMENTS),
            where('assignedTo', 'array-contains', uid),
          ));
          const open = snap.docs.find((d) => {
            const mine = ((d.data().completions || []) as Array<{ officerUid: string; status: string }>)
              .find((c) => c.officerUid === uid);
            return mine && mine.status !== 'completed';
          });
          if (open) {
            if (!cancelled) setLine("Today's focus: your assigned scenario is waiting in Incoming.");
            return;
          }
        } catch (e) { reportCrash(e); /* next source */ }
      }

      // 3. A live special event.
      try {
        const res = await httpsCallable(functions, 'listSpecialEvents')({});
        const events = (res.data as { data?: Array<{ title: string }> }).data || [];
        if (events.length > 0 && events[0]) {
          if (!cancelled) setLine(`Today's focus: special event live — ${events[0].title}.`);
          return;
        }
      } catch (e) { reportCrash(e); /* next source */ }

      // 4. The next tier target from the standing block.
      const ref = doc(db, COLLECTIONS.OFFICERS, uid, SUBCOLLECTIONS.INSIGHTS_AGGREGATED, 'latest');
      unsubStanding = onSnapshot(ref, (snap) => {
        const cert = snap.data()?.certification as
          | { next: { name: string; threshold: number; window: number } | null }
          | undefined;
        if (cancelled) return;
        if (cert?.next) {
          setLine(`Today's focus: hold ${cert.next.threshold.toFixed(1)}/10 over your last ${cert.next.window} to reach ${cert.next.name}.`);
        } else {
          setLine('Today’s focus: take a call — every scored call moves your standing.');
        }
        unsubStanding?.();
      }, () => { /* the line simply doesn't render */ });
    })();

    return () => {
      cancelled = true;
      unsubStanding?.();
    };
  }, [user?.uid, departmentId]);

  if (!line) return null;

  return (
    <p data-testid="briefing-line" className="mb-4 -mt-1 text-xs text-copapp-300">
      {line}
    </p>
  );
}
